/**
 * useApprovalQueue Hook
 *
 * FERP CRITICAL: Template approval queue management for life-critical AI templates
 * Dual-control approval workflow with criticality-based routing
 *
 * Principles:
 * - All approval data from backend (GraphQL query)
 * - Criticality-based prioritization (life-critical → critical → operational)
 * - Deadline tracking with automatic escalation
 * - Repository pattern (no direct API calls)
 * - Real-time polling for queue updates
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCallback, useMemo } from 'react';

// ============================================================
// GraphQL Queries
// ============================================================

/**
 * Get Approval Queue Query
 *
 * Backend: graphql_gateway/src/metadata_api/compliance_resolvers.rs
 * Returns: Pending approval workflow instances with criticality-based filtering
 */
export const GET_APPROVAL_QUEUE = gql`
  query GetApprovalQueue($tenantId: String!, $filter: ApprovalQueueFilter) {
    getApprovalQueue(tenantId: $tenantId, filter: $filter) {
      instances {
        instanceId
        templateId
        templateName
        templateDomain
        status
        criticalityLevel
        submittedAt
        submittedBy
        submitterName
        deadline
        requiredApprovers {
          userId
          name
          role
          expertise
          hasApproved
          approvedAt
        }
        approvalProgress {
          required
          completed
        }
        validationSummary {
          success
          totalViolations
          errorCount
          warningCount
        }
      }
      totalPending
      overdueCount
      criticalCount
      lifeCriticalCount
    }
  }
`;

// ============================================================
// TypeScript Types
// ============================================================

export interface ApprovalQueueFilter {
  criticalityLevel?: 'LifeCritical' | 'Critical' | 'Operational' | 'All';
  deadline?: 'Urgent' | 'Normal' | 'Routine' | 'All';
  status?: 'PendingReview' | 'Approved' | 'Rejected' | 'RevisionRequested' | 'All';
  domain?: string;
  submitterId?: string;
}

export interface ApproverInfo {
  userId: string;
  name: string;
  role: string;
  expertise: string[];
  hasApproved: boolean;
  approvedAt?: string;
}

export interface ApprovalProgress {
  required: number; // e.g., 2 for dual control
  completed: number; // e.g., 1 if one approver done
}

export interface ValidationSummary {
  success: boolean;
  totalViolations: number;
  errorCount: number;
  warningCount: number;
}

export interface ApprovalWorkflowInstance {
  instanceId: string;
  templateId: string;
  templateName: string;
  templateDomain: string;
  status: 'PendingReview' | 'Approved' | 'Rejected' | 'RevisionRequested';
  criticalityLevel: 'LifeCritical' | 'Critical' | 'Operational';
  submittedAt: string;
  submittedBy: string;
  submitterName: string;
  deadline: string;
  requiredApprovers: ApproverInfo[];
  approvalProgress: ApprovalProgress;
  validationSummary: ValidationSummary;
}

export interface ApprovalQueueData {
  instances: ApprovalWorkflowInstance[];
  totalPending: number;
  overdueCount: number;
  criticalCount: number;
  lifeCriticalCount: number;
}

// ============================================================
// Hook Implementation
// ============================================================

/**
 * useApprovalQueue Hook
 *
 * Manages template approval queue with real-time updates
 *
 * Features:
 * - Real-time polling (10s interval)
 * - Criticality-based filtering
 * - Deadline tracking
 * - Approval progress calculation
 * - Overdue detection
 */
export function useApprovalQueue(
  tenantId: string,
  filter?: ApprovalQueueFilter,
  pollInterval = 10000 // 10 seconds
) {
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_APPROVAL_QUEUE, {
    variables: { tenantId, filter },
    pollInterval, // Automatic polling for real-time updates
    skip: !tenantId,
  });

  // Extract queue data
  const queueData: ApprovalQueueData = useMemo(() => {
    if (!data?.getApprovalQueue) {
      return {
        instances: [],
        totalPending: 0,
        overdueCount: 0,
        criticalCount: 0,
        lifeCriticalCount: 0,
      };
    }
    return data?.getApprovalQueue ?? null;
  }, [data]);

  /**
   * Get instances by criticality level
   */
  const getInstancesByCriticality = useCallback(
    (criticality: 'LifeCritical' | 'Critical' | 'Operational') => {
      return queueData.instances.filter(
        (instance) => instance.criticalityLevel === criticality
      );
    },
    [queueData.instances]
  );

  /**
   * Get overdue instances
   */
  const getOverdueInstances = useCallback(() => {
    const now = new Date().toISOString();
    return queueData.instances.filter((instance) => instance.deadline < now);
  }, [queueData.instances]);

  /**
   * Get urgent instances (<24h remaining)
   */
  const getUrgentInstances = useCallback(() => {
    const now = Date.now();
    const urgentThreshold = 24 * 60 * 60 * 1000; // 24 hours in ms

    return queueData.instances.filter((instance) => {
      const deadline = new Date(instance.deadline).getTime();
      const timeRemaining = deadline - now;
      return timeRemaining > 0 && timeRemaining < urgentThreshold;
    });
  }, [queueData.instances]);

  /**
   * Calculate time remaining for instance deadline
   */
  const getTimeRemaining = useCallback((instance: ApprovalWorkflowInstance) => {
    const now = Date.now();
    const deadline = new Date(instance.deadline).getTime();
    const timeRemaining = deadline - now;

    if (timeRemaining < 0) {
      return {
        isOverdue: true,
        hours: 0,
        minutes: 0,
        label: 'OVERDUE',
        color: 'red',
      };
    }

    const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));

    const isUrgent = hours < 24;

    return {
      isOverdue: false,
      hours,
      minutes,
      label: `${hours}h ${minutes}m remaining`,
      color: hours < 6 ? 'red' : isUrgent ? 'orange' : 'gray',
    };
  }, []);

  /**
   * Get approval progress percentage
   */
  const getApprovalProgressPercentage = useCallback(
    (instance: ApprovalWorkflowInstance) => {
      const { required, completed } = instance.approvalProgress;
      return required > 0 ? (completed / required) * 100 : 0;
    },
    []
  );

  /**
   * Check if instance requires dual control
   */
  const requiresDualControl = useCallback((instance: ApprovalWorkflowInstance) => {
    return instance.criticalityLevel === 'LifeCritical' && instance.approvalProgress.required >= 2;
  }, []);

  /**
   * Get pending approvers (not yet approved)
   */
  const getPendingApprovers = useCallback((instance: ApprovalWorkflowInstance) => {
    return instance.requiredApprovers.filter((approver) => !approver.hasApproved);
  }, []);

  /**
   * Get completed approvers
   */
  const getCompletedApprovers = useCallback((instance: ApprovalWorkflowInstance) => {
    return instance.requiredApprovers.filter((approver) => approver.hasApproved);
  }, []);

  /**
   * Sort instances by priority
   * Priority: Overdue > Life-Critical + Urgent > Critical + Urgent > Life-Critical > Critical > Operational
   */
  const sortedInstances = useMemo(() => {
    return [...queueData.instances].sort((a, b) => {
      const aRemaining = getTimeRemaining(a);
      const bRemaining = getTimeRemaining(b);

      // Overdue instances first
      if (aRemaining.isOverdue && !bRemaining.isOverdue) return -1;
      if (!aRemaining.isOverdue && bRemaining.isOverdue) return 1;

      // Then by criticality
      const criticalityOrder = {
        LifeCritical: 3,
        Critical: 2,
        Operational: 1,
      };

      const aCriticality = criticalityOrder[a.criticalityLevel];
      const bCriticality = criticalityOrder[b.criticalityLevel];

      if (aCriticality !== bCriticality) {
        return bCriticality - aCriticality;
      }

      // Then by deadline (earliest first)
      return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
    });
  }, [queueData.instances, getTimeRemaining]);

  return {
    // Data
    queue: sortedInstances,
    totalPending: queueData.totalPending,
    overdueCount: queueData.overdueCount,
    criticalCount: queueData.criticalCount,
    lifeCriticalCount: queueData.lifeCriticalCount,
    loading,
    error,

    // Actions
    refetch,

    // Helpers
    getInstancesByCriticality,
    getOverdueInstances,
    getUrgentInstances,
    getTimeRemaining,
    getApprovalProgressPercentage,
    requiresDualControl,
    getPendingApprovers,
    getCompletedApprovers,
  };
}
